import { useMemo } from 'react';

import type { UserFilterParams } from '@/types/api';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CompanyAttribute, CompanyManager } from '@/types/directory/Company';
import type { User } from '@/types/directory/User';

import {
    useGetCompanyInformation,
    useGetCompanyPeople,
    useGetUserPermissions,
} from '@/api/directory';
import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { getCompanyAttributeList } from '@/utils/company';
import { transformCriteriaToFilters } from '@/utils/cycleEvaluation';

interface UseRevieweesFromAudienceParams {
    cycleEvaluation: CycleEvaluation | undefined;
}

const EMPTY_USERS: User[] = [];

export const useRevieweesFromAudience = ({
    cycleEvaluation,
}: UseRevieweesFromAudienceParams): User[] => {
    const { data: companyInformation } = useGetCompanyInformation();
    const { data: permissions } = useGetUserPermissions();

    const filterParams: UserFilterParams = useMemo(() => {
        const isFilterByAttributes =
            cycleEvaluation?.cycleAudience?.cycleAudienceType ===
            CycleAudienceType.FILTER_BY_ATTRIBUTES;

        if (!isFilterByAttributes || !cycleEvaluation?.cycleAudience?.cycleAudienceCriterias) {
            return {};
        }

        const companyAttributes = getCompanyAttributeList(companyInformation);

        const { filters } = transformCriteriaToFilters({
            criterias: cycleEvaluation.cycleAudience.cycleAudienceCriterias,
            companyAttributes,
        });

        const toIds = (items: Array<CompanyAttribute | CompanyManager>) =>
            items.map((i) => i.id).filter((id): id is string => !!id);

        const ifNotEmpty = (items: Array<CompanyAttribute | CompanyManager>) =>
            items.length > 0 ? toIds(items) : undefined;

        return {
            departmentIds: ifNotEmpty(filters.departments),
            jobTitleIds: ifNotEmpty(filters.jobTitles),
            managerIds: ifNotEmpty(filters.managers),
            companyAddressIds: ifNotEmpty(filters.companyAddresses),
            employeeLevelIds: ifNotEmpty(filters.employeeLevels),
            customAttributeIds: ifNotEmpty(filters.customAttributes),
        };
    }, [
        cycleEvaluation?.cycleAudience?.cycleAudienceType,
        cycleEvaluation?.cycleAudience?.cycleAudienceCriterias,
        companyInformation,
    ]);

    const { data: companyPeopleData } = useGetCompanyPeople(
        {
            pageNumber: 1,
            pageSize: 50,
            isActiveOnly: true,
            ...filterParams,
        },
        {
            enabled: !!cycleEvaluation,
            canViewResultsOfAllDepartments: permissions?.canViewResultsOfAllDepartments,
        },
    );

    return companyPeopleData?.items ?? EMPTY_USERS;
};
